import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Star, Check, EyeOff, X, Trash2, Eye } from "lucide-react";

const S_TONE = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700", hidden: "bg-slate-200 text-slate-500" };
const inp = "border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white";

function Stars({ n }) {
  return <span className="inline-flex" aria-label={`${n} out of 5 stars`}>{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />)}</span>;
}

function ActionButtons({ r, setStatus, del, size = "sm" }) {
  const cls = size === "lg" ? "px-3 py-2 text-xs font-semibold uppercase tracking-wider" : "p-1.5";
  return (
    <>
      <button title="Approve" data-testid={`review-approve-${r.id}`} onClick={() => setStatus(r.id, "approved")} className={`${cls} border border-slate-200 rounded-sm text-emerald-600 inline-flex items-center gap-1`}><Check className="w-4 h-4" />{size === "lg" && "Approve"}</button>
      <button title="Hide" data-testid={`review-hide-${r.id}`} onClick={() => setStatus(r.id, "hidden")} className={`${cls} border border-slate-200 rounded-sm text-slate-500 inline-flex items-center gap-1`}><EyeOff className="w-4 h-4" />{size === "lg" && "Hide"}</button>
      <button title="Reject" data-testid={`review-reject-${r.id}`} onClick={() => setStatus(r.id, "rejected")} className={`${cls} border border-slate-200 rounded-sm text-[#E63946] inline-flex items-center gap-1`}><X className="w-4 h-4" />{size === "lg" && "Reject"}</button>
      <button title="Delete" data-testid={`review-delete-${r.id}`} onClick={() => del(r.id)} className={`${cls} border border-slate-200 rounded-sm text-slate-400 inline-flex items-center gap-1`}><Trash2 className="w-4 h-4" />{size === "lg" && "Delete"}</button>
    </>
  );
}

export default function MarketplaceReviewsAdmin() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [productId, setProductId] = useState("");
  const [rating, setRating] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/marketplace/reviews", {
        params: { status: filter || undefined, product_id: productId || undefined, rating: rating || undefined },
      });
      setItems(data.items); setCounts(data.counts); setProducts(data.products || []);
    } finally { setLoading(false); }
  }, [filter, productId, rating]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { try { await api.post(`/admin/marketplace/reviews/${id}/status`, { status }); toast.success(`Review ${status}`); setDetail(null); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this review permanently?")) return; try { await api.delete(`/admin/marketplace/reviews/${id}`); toast.success("Deleted"); setDetail(null); load(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="mp-reviews-admin">
      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2 mb-3">
        {["", "pending", "approved", "rejected", "hidden"].map((s) => (
          <button key={s || "all"} data-testid={`review-filter-${s || "all"}`} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${filter === s ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-500 border-slate-200"}`}>
            {s || "All"}{s && counts[s] != null ? ` (${counts[s]})` : ""}
          </button>
        ))}
      </div>
      {/* Product + rating filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="review-product-filter" value={productId} onChange={(e) => setProductId(e.target.value)} className={`${inp} max-w-[240px]`}>
          <option value="">All products</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="review-rating-filter" value={rating} onChange={(e) => setRating(e.target.value)} className={inp}>
          <option value="">All ratings</option>
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
        </select>
        {(productId || rating || filter) && <button data-testid="review-clear-filters" onClick={() => { setFilter(""); setProductId(""); setRating(""); }} className="text-xs text-slate-400 hover:text-[#E63946] underline">Clear filters</button>}
      </div>

      {loading ? <div className="py-10 text-center text-slate-400">Loading…</div>
        : items.length === 0 ? <div className="py-10 text-center text-slate-400" data-testid="reviews-empty">No reviews{filter ? ` (${filter})` : ""} yet.</div>
        : (
          <div className="overflow-x-auto border border-slate-200 rounded-sm bg-white">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-3 py-2">Product</th><th className="px-3 py-2">Reviewer</th><th className="px-3 py-2">Rating</th>
                  <th className="px-3 py-2">Title</th><th className="px-3 py-2">Submitted</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} data-testid={`review-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-[#0A192F]">{r.product_name}</td>
                    <td className="px-3 py-2">{r.reviewer_name}{r.verified_purchase && <span className="ml-1 text-[9px] font-bold uppercase text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded-sm">Verified</span>}</td>
                    <td className="px-3 py-2"><Stars n={r.rating} /></td>
                    <td className="px-3 py-2 max-w-[220px] truncate font-medium text-[#0A192F]">{r.title}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${S_TONE[r.status]}`}>{r.status}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button title="View" data-testid={`review-view-${r.id}`} onClick={() => setDetail(r)} className="p-1.5 border border-slate-200 rounded-sm text-[#0A192F]"><Eye className="w-4 h-4" /></button>
                        <ActionButtons r={r} setStatus={setStatus} del={del} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4 bg-black/50" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-sm w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()} data-testid="review-detail-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2"><Stars n={detail.rating} /><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${S_TONE[detail.status]}`}>{detail.status}</span></div>
                <h3 className="font-display text-lg uppercase tracking-tight mt-2 text-[#0A192F]">{detail.title}</h3>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400" data-testid="review-detail-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs text-slate-500 mt-1">{detail.reviewer_name}{detail.verified_purchase ? " · Verified purchase" : ""} · {detail.product_name} · {(detail.created_at || "").slice(0, 10)}</div>
            <p className="text-sm text-slate-700 mt-3 whitespace-pre-line leading-relaxed">{detail.body}</p>
            <div className="flex flex-wrap gap-2 mt-5 justify-end border-t border-slate-100 pt-4">
              <ActionButtons r={detail} setStatus={setStatus} del={del} size="lg" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
